/*
 * Legal page content (Terms of Use & Privacy Policy).
 *
 * Each document is an array of sections. A section looks like:
 *   { heading: "1. Section title", body: ["First paragraph.", "Second paragraph."] }
 *
 * - `body` may be a single string or an array of mixed items:
 *     - A string renders as a <p> paragraph.
 *     - An array of strings renders as a <ul> bullet list.
 */

export const LEGAL_EFFECTIVE_DATE = "25 June 2026";

export const TERMS_SECTIONS = [
    {
        heading: "1. Introduction and Acceptance",
        body: [
            "Welcome to TravelBuddy (\"we\", \"us\", \"our\"). TravelBuddy is a web-based travel planning application that allows registered users to create, manage, and track personal travel itineraries.",
            "These Terms of Use (\"Terms\") govern your access to and use of the TravelBuddy website located at thetravelbuddy.com.au and all associated services (collectively, the \"Service\"). By creating an account or using the Service in any way, you agree to be bound by these Terms and our Privacy Policy.",
            "If you do not agree to these Terms, you must not use the Service.",
            "TravelBuddy is a personal project developed and operated by Ollie Le, located in Victoria, Australia.",
        ],
    },
    {
        heading: "2. Eligibility",
        body: [
            "You must be at least 13 years of age to use the Service. By using the Service, you represent and warrant that you meet this age requirement. If you are under 18, you represent that you have obtained the consent of a parent or legal guardian to use the Service.",
            "The Service is intended for personal, non-commercial use unless otherwise agreed in writing with us.",
        ],
    },
    {
        heading: "3. Account Registration",
        body: [
            "To access the core features of the Service, you must create an account by providing a valid email address, a username, and a password. You may optionally upload a profile picture.",
            "You agree to:",
            [
                "Provide accurate, current, and complete information during registration;",
                "Keep your login credentials confidential and not share them with any third party;",
                "Notify us immediately at info@thetravelbuddy.com.au if you suspect unauthorised access to your account;",
                "Take responsibility for all activity that occurs under your account.",
            ],
            "We reserve the right to suspend or terminate accounts where we have reasonable grounds to believe registration information is false, misleading, or in violation of these Terms.",
        ],
    },
    {
        heading: "4. The Service",
        body: [
            "TravelBuddy provides the following core features and services:",
        ],
    },
    {
        heading: "4.1 Trip Itinerary Planning",
        body: [
            "TravelBuddy allows you to:",
            [
                "Create travel trips with a title, destination countries, and date range;",
                "Build a day-by-day itinerary for each trip, with individual activity rows organised across customisable columns (including time, place, category, price, payer, and notes);",
                "Edit, reorder, and delete itinerary items and columns via drag-and-drop;",
                "Add, remove, and rename custom columns and assign column types (text, time, number, tags, URL, etc.);",
                "Copy and paste rows within or across days;",
                "Apply column changes to a single day or to all days simultaneously;",
                "Duplicate a full trip;",
                "Delete a trip permanently.",
            ],
        ],
    },
    {
        heading: "4.2 Cost and Spending Tracking",
        body: [
            "The Service provides tools to record estimated costs against individual itinerary items. You can view a cost summary and cost breakdown across your trips. Cost figures are for personal planning purposes only. TravelBuddy does not process, hold, or transfer any money on your behalf, and all cost entries are user-supplied estimates.",
        ],
    },
    {
        heading: "4.3 Travel Statistics",
        body: [
            "The Service calculates and displays personal travel statistics derived from your saved trips, including the number of countries visited, total days travelled, and total trips planned. These statistics are computed automatically from the data you have entered and are provided for informational and personal tracking purposes only.",
        ],
    },
    {
        heading: "4.4 Image Uploads",
        body: [
            "You may upload images in connection with your profile (profile picture) and trip locations. Uploaded images are stored on Amazon Web Services (AWS) Simple Storage Service (S3). By uploading an image, you grant us a non-exclusive, royalty-free licence to store and display that image solely for the purposes of operating the Service for you. You are solely responsible for ensuring you have the right to upload any image you submit.",
        ],
    },
    {
        heading: "4.5 Help and Enquiries",
        body: [
            "The Service includes a Help page where you can browse Frequently Asked Questions and submit an enquiry form. When you submit an enquiry, you provide your name, email address, and message. This information is used solely to respond to your enquiry.",
        ],
    },
    {
        heading: "4.6 Account and Profile Settings",
        body: [
            "You may update your profile information (first name, last name, email address, username, about text, and profile picture) and change your password through the Settings page. You may also request deletion of your account (see Section 9).",
        ],
    },
    {
        heading: "4.7 Social Sign-In",
        body: [
            "In addition to registering with an email and password, you may choose to create an account or sign in using Google or Facebook (\"Social Login\").",
            "By using Social Login, you authorise us to access certain profile information from that provider (typically your name and email address) in accordance with that provider's permissions model. We will use this information only to create and manage your TravelBuddy account.",
            "You are responsible for the security of your Social Login credentials with the respective provider. We are not responsible for any breach of your Social Login account.",
        ],
    },
    {
        heading: "4.8 Trip Sharing",
        body: [
            "TravelBuddy allows you to invite other registered users to access your trips. Invited members may be assigned one of the following roles:",
            [
                "Owner: full control, including the ability to edit, share, and delete the trip.",
                "Editor: can view and edit all itinerary content.",
                "Viewer: can view itinerary content only, with no editing rights.",
            ],
            "By sharing a trip, you acknowledge that:",
            [
                "Invited members will be able to see all content within that trip, including itinerary items, costs, and any notes you have entered.",
                "You are responsible for ensuring you have the right to share any content in a shared trip with the people you invite.",
                "We are not responsible for how other users access, use, or store information from trips shared with them.",
            ],
            "You may revoke a member's access to your trip at any time. Revocation takes effect immediately, after which the former member will no longer be able to view or edit the trip.",
        ],
    },
    {
        heading: "4.9 Cost Splitting",
        body: [
            "TravelBuddy provides tools to help you record who paid for an activity and how costs are divided among trip members (\"Cost Splitting\"). These tools are for personal planning and informational purposes only.",
            "Important limitations:",
            [
                "TravelBuddy does not process, hold, transfer, or settle any money on behalf of any user. Any financial settlement between group members is entirely the responsibility of those members.",
                "Cost splits calculated by the Service are estimates only. We make no representation that any calculated split is legally enforceable or financially binding between trip members.",
                "If you rely on Cost Splitting data to settle costs with others, you do so at your own risk.",
            ],
            "Nothing in this section constitutes financial, accounting, or legal advice.",
        ],
    },
    {
        heading: "5. User Content",
        body: [
            "\"User Content\" means all data, text, images, and other material that you submit, upload, or create through the Service, including trip titles, itinerary items, and profile information.",
            "You retain ownership of your User Content. By submitting User Content to the Service, you grant us a limited, non-exclusive, royalty-free, worldwide licence to store, process, and display your User Content solely to provide the Service to you.",
            "You represent and warrant that:",
            [
                "You own or have the necessary rights and permissions to submit your User Content;",
                "Your User Content does not infringe any third-party intellectual property rights, privacy rights, or any applicable law;",
                "Your User Content does not contain any unlawful, offensive, defamatory, or harmful material.",
            ],
            "We do not routinely review User Content, but reserve the right to remove any User Content that violates these Terms or applicable law.",
        ],
    },
    {
        heading: "6. Acceptable Use",
        body: [
            "You agree to use the Service only for lawful purposes and in accordance with these Terms. You must not:",
            [
                "Use the Service in any way that violates applicable Australian federal, state, or local law or regulation;",
                "Attempt to gain unauthorised access to any part of the Service, its servers, or any system or network connected to the Service;",
                "Transmit any malware, viruses, or other harmful code;",
                "Scrape, crawl, or extract data from the Service by automated means without our prior written consent;",
                "Use the Service to harass, abuse, or harm another person;",
                "Impersonate any person or entity or misrepresent your affiliation with any person or entity;",
                "Attempt to reverse engineer, decompile, or disassemble any part of the Service;",
                "Use the Service for any commercial purpose without our prior written agreement.",
            ],
        ],
    },
    {
        heading: "7. Privacy and Data",
        body: [
            "Your use of the Service is subject to our Privacy Policy, which is incorporated into these Terms by reference and available at https://thetravelbuddy.com.au/privacy.",
            "In summary, the following personal information is collected and stored when you use the Service:",
            [
                "Account information: email address, username, first name, last name, password (stored as a hashed value — your plain-text password is never stored), and optional profile picture URL.",
                "Trip data: trip titles, destinations, dates, and all itinerary content you enter (places, times, categories, prices, notes, etc.).",
                "Usage identifiers: a user ID is stored in your browser's local storage to maintain your session.",
            ],
            "Your trip details are private to your account. We do not sell your personal data to third parties.",
            "Account and trip data is stored on Render's cloud infrastructure in the United States. Uploaded images are stored on AWS S3 in the Asia Pacific (Sydney) region, Australia.",
        ],
    },
    {
        heading: "8. Intellectual Property",
        body: [
            "All content, features, and functionality of the Service that are not User Content — including but not limited to the TravelBuddy name and logo, software, design, text, graphics, and underlying code — are owned by or licensed to us and are protected by Australian and international copyright, trademark, and other intellectual property laws.",
            "You may not reproduce, distribute, modify, create derivative works of, publicly display, or otherwise exploit any part of the Service for any commercial purpose without our prior written consent.",
            "Nothing in these Terms transfers any intellectual property rights to you beyond the limited licence to use the Service described herein.",
        ],
    },
    {
        heading: "9. Account Deletion",
        body: [
            "You may request deletion of your account at any time through the Settings page (Account and Password > Delete account). Upon deletion:",
            [
                "Your account and all associated personal information (including your profile, trip data, and itinerary items) will be permanently deleted from our systems.",
                "Deleted data cannot be recovered. You will need to create a new account if you wish to use the Service again in the future.",
            ],
            "We may also suspend or terminate your account if you breach these Terms, if required by law, or if we discontinue the Service.",
        ],
    },
    {
        heading: "10. Third-Party Services",
        body: [
            "The Service integrates with the following third-party services:",
            [
                "Amazon Web Services (AWS) S3: used to store uploaded images. Your use of image upload features is subject to AWS's terms of service and privacy policy.",
                "Google Sign-In: used to allow you to create an account or sign in using your Google account. Your use of Google Sign-In is subject to Google's Terms of Service and Privacy Policy.",
                "Facebook Login: used to allow you to create an account or sign in using your Facebook account. Your use of Facebook Login is subject to Meta's Terms of Service and Privacy Policy.",
                "Render: our cloud infrastructure provider for backend services and database hosting. Render processes data on our behalf as a service provider.",
            ],
            "We are not responsible for the content, privacy practices, or availability of any third-party services.",
        ],
    },
    {
        heading: "11. Disclaimers",
        body: [
            "THE SERVICE IS PROVIDED \"AS IS\" AND \"AS AVAILABLE\", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED.",
            "TO THE FULLEST EXTENT PERMITTED BY AUSTRALIAN CONSUMER LAW AND OTHER APPLICABLE LEGISLATION, WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.",
            "In particular:",
            [
                "All itinerary content, cost estimates, and travel statistics in the Service are entered by you and are for personal planning purposes only. TravelBuddy makes no representations regarding the accuracy, completeness, or suitability of any such information for any travel or financial decision.",
                "We do not guarantee that the Service will be available at all times, error-free, or free from viruses or other harmful components.",
            ],
            "Nothing in these Terms excludes, restricts, or modifies any consumer guarantee, right, or remedy conferred by the Australian Consumer Law (ACL) that cannot lawfully be excluded, restricted, or modified.",
        ],
    },
    {
        heading: "12. Limitation of Liability",
        body: [
            "TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL WE BE LIABLE TO YOU FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE, HOWEVER CAUSED AND UNDER ANY THEORY OF LIABILITY, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.",
            "OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU HAVE PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED AUSTRALIAN DOLLARS (AUD $100.00).",
            "These limitations apply to the fullest extent permitted by applicable law. Where statutory protections under the ACL apply and cannot be excluded, our liability is limited to the extent permitted by those protections.",
        ],
    },
    {
        heading: "13. Indemnification",
        body: [
            "You agree to indemnify, defend, and hold harmless TravelBuddy, its officers, directors, employees, contractors, and agents from and against any claims, liabilities, damages, losses, costs, and expenses (including reasonable legal fees) arising out of or relating to:",
            [
                "Your use of or inability to use the Service;",
                "Your User Content;",
                "Your violation of these Terms;",
                "Your violation of any rights of a third party.",
            ],
        ],
    },
    {
        heading: "14. Changes to These Terms",
        body: [
            "We may update these Terms from time to time to reflect changes to the Service, applicable law, or our practices. When we make material changes, we will update the \"Last updated\" date at the top of this document. We may also notify you by email or via a notice within the Service.",
            "Your continued use of the Service after any changes become effective constitutes your acceptance of the revised Terms. If you do not agree to the revised Terms, you must stop using the Service.",
        ],
    },
    {
        heading: "15. Changes to the Service",
        body: [
            "We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with or without notice. We will not be liable to you or any third party for any modification, suspension, or discontinuation of the Service.",
            "TravelBuddy is currently free to use. We may introduce paid or premium features in the future. Where we do, we will provide clear notice of any applicable fees before you are charged.",
        ],
    },
    {
        heading: "16. Governing Law and Dispute Resolution",
        body: [
            "These Terms are governed by and construed in accordance with the laws of Victoria, Australia, without regard to conflict of law principles.",
            "Any dispute arising out of or in connection with these Terms shall first be attempted to be resolved by good-faith negotiation between the parties. If the dispute cannot be resolved by negotiation within 30 days, either party may refer the matter to the courts of Victoria, Australia.",
            "Nothing in this clause prevents either party from seeking urgent injunctive or other equitable relief.",
        ],
    },
    {
        heading: "17. Contact Us",
        body: [
            "If you have any questions, concerns, or complaints about these Terms or the Service, please contact us:",
            "Email: info@thetravelbuddy.com.au",
            "Phone: +61 402 420 062",
            "Website: thetravelbuddy.com.au",
            "We aim to respond to all enquiries within 2 business days.",
        ],
    },
];

export const PRIVACY_SECTIONS = [
    {
        heading: "1. Introduction",
        body: [
            "TravelBuddy (\"we\", \"us\", \"our\") is committed to protecting the privacy of the people who use our website at thetravelbuddy.com.au and related services (collectively, the \"Service\").",
            "This Privacy Policy explains what personal information we collect when you use the Service, why we collect it, how we use and protect it, and the rights you have over it.",
            "This Privacy Policy should be read together with our Terms of Use. Capitalised terms not defined here have the meaning given to them in the Terms of Use.",
            "TravelBuddy is a personal project developed and operated by Ollie Le, located in Victoria, Australia. We are bound by the Privacy Act 1988 (Cth) and the Australian Privacy Principles.",
        ],
    },
    {
        heading: "2. What Information We Collect",
        body: [
            "We only collect personal information that is reasonably necessary to provide the Service to you.",
        ],
    },
    {
        heading: "2.1 Information You Provide When Creating an Account",
        body: [
            "When you sign up for a TravelBuddy account, you provide:",
            [
                "Your email address;",
                "A username;",
                "A password (which we store only as a securely hashed value — we never store your plain-text password);",
                "Optionally, a profile picture (an image you choose to upload).",
            ],
        ],
    },
    {
        heading: "2.2 Information You Provide in Your Profile",
        body: [
            "You may also choose to provide the following profile information through the Settings page:",
            [
                "Your first name;",
                "Your last name;",
                "A short \"About\" description;",
                "An updated profile picture.",
            ],
            "This information is optional. You may leave any of these fields blank.",
        ],
    },
    {
        heading: "2.3 Information You Provide When Planning Trips",
        body: [
            "When you create or edit a trip, you provide:",
            [
                "A trip title;",
                "One or more destination countries;",
                "Trip start and end dates;",
                "Itinerary items, which may include times, places, categories, prices, payer names, notes, links, and any other content you choose to enter in customisable columns;",
                "Optional location images you upload.",
            ],
            "The information in your trips is whatever you choose to enter. You should not enter sensitive personal information into your trips that you do not want stored on the Service.",
        ],
    },
    {
        heading: "2.4 Information You Provide When Contacting Us",
        body: [
            "When you submit the enquiry form on the Help page, you provide:",
            [
                "Your first name;",
                "Your last name;",
                "Your email address;",
                "The content of your message.",
            ],
            "We use this information solely to respond to your enquiry. If we end up treating any enquiry as a privacy complaint, we will retain the relevant correspondence in accordance with our complaints process (see Section 9).",
        ],
    },
    {
        heading: "2.5 Information We Collect Automatically",
        body: [
            "When you use the Service, the following technical information is collected automatically:",
            [
                "A user identifier (\"userId\"), which we store in your browser's local storage so that the Service can recognise you across page loads. This identifier is also sent to our backend on every API request.",
                "Standard server logs, which may include your IP address, browser type and version, operating system, the pages you visit on the Service, and timestamps. We use these logs to operate, secure, and debug the Service.",
            ],
            "We do not currently use cookies for tracking or advertising purposes, and we do not use any third-party analytics tools. If this changes in the future, we will update this Privacy Policy to disclose what data is collected.",
        ],
    },
    {
        heading: "2.6 Information We Receive from Social Sign-In Providers",
        body: [
            "If you choose to sign in or sign up using Google or Facebook, we will receive certain personal information from that provider as part of the OAuth authorisation flow. The information we receive depends on the provider and the permissions you grant, but typically includes:",
            [
                "Your name (as held by the provider);",
                "Your email address;",
                "A unique identifier issued by the provider;",
                "In some cases, a profile picture URL.",
            ],
            "We use this information only to create and manage your TravelBuddy account. We do not access any other information held by the third-party provider, including your contacts, posts, or other social data.",
            "The provider's own privacy policy applies to information they hold about you. We recommend you review the relevant provider's privacy policy before using Social Login.",
            "You can revoke TravelBuddy's access to your Social Login at any time through the relevant provider's account settings.",
        ],
    },
    {
        heading: "2.7 Information We Do NOT Collect",
        body: [
            "We currently do not collect:",
            [
                "Payment card or bank account information (the Service is free, and we do not process any payments);",
                "Precise device location data (we do not request access to your device's GPS);",
                "Health, biometric, or financial information (other than the cost estimates that you choose to enter into your trips, which are considered planning data rather than financial data).",
            ],
        ],
    },
    {
        heading: "3. How We Use Your Information",
        body: [
            "We only use your personal information for the purposes for which it was collected, including:",
            [
                "To create and manage your account and let you sign in;",
                "To provide the core trip planning features of the Service (saving, loading, editing, duplicating, and deleting your trips);",
                "To calculate and display your travel statistics (countries visited, days travelled, trips planned);",
                "To respond to enquiries you submit through the Help page;",
                "To process account changes you request, including profile updates, password changes, and account deletion;",
                "To operate, maintain, secure, and improve the Service (including debugging, fraud prevention, and abuse prevention);",
                "To comply with our legal obligations and enforce our Terms of Use.",
            ],
            "We will not use your personal information for any other purpose without either obtaining your consent or being permitted or required to do so by law.",
            "We do not sell your personal information to anyone.",
            "We do not use your personal information for advertising or marketing purposes.",
        ],
    },
    {
        heading: "4. How We Store and Secure Your Information",
        body: [],
    },
    {
        heading: "4.1 Where Your Information Is Stored",
        body: [
            "Your information is stored as follows:",
            [
                "Account and trip data is stored in our PostgreSQL database, hosted on Render (render.com), a cloud infrastructure provider with servers located in the United States.",
                "Uploaded images (profile pictures and trip location images) are stored on Amazon Web Services (AWS) Simple Storage Service (S3) in the Asia Pacific (Sydney) region (ap-southeast-2), located in Australia.",
            ],
        ],
    },
    {
        heading: "4.2 How We Secure Your Information",
        body: [
            "We take reasonable steps to protect personal information from misuse, interference, loss, unauthorised access, modification, and disclosure, including:",
            [
                "Storing user passwords only as securely hashed values, never in plain text;",
                "Restricting access to our backend systems to authorised personnel;",
                "Using HTTPS encryption for traffic between your browser and the Service;",
                "Generating random unique filenames for uploaded images.",
            ],
            "No method of transmission or storage over the internet is 100% secure. While we work to protect your personal information, we cannot guarantee absolute security.",
        ],
    },
    {
        heading: "4.3 Data Breach Notification",
        body: [
            "If a data breach occurs that is likely to result in serious harm to you, we will notify you and the Office of the Australian Information Commissioner (OAIC) as required under the Notifiable Data Breaches scheme.",
        ],
    },
    {
        heading: "5. Who We Share Your Information With",
        body: [
            "We do not sell or rent your personal information.",
            "We share personal information only with the following categories of third parties, and only to the extent necessary to operate the Service:",
        ],
    },
    {
        heading: "5.1 Cloud Infrastructure Providers",
        body: [
            "We use the following cloud infrastructure providers to operate the Service:",
            [
                "Amazon Web Services (AWS): we use AWS S3 to store the images you upload (profile pictures and trip location images). AWS processes this data on our behalf as a service provider. Your use of image uploads is subject to AWS's privacy policy in addition to ours.",
                "Render: we use Render to host our backend server and PostgreSQL database. Render processes data on our behalf as a service provider. Render's infrastructure is located in the United States.",
            ],
        ],
    },
    {
        heading: "5.2 Other Service Users",
        body: [
            "Your account information is private to you. We do not show your account details (email address, last name, \"About\" text, etc.) to other users.",
            "If you invite another registered user to access a trip, the following information becomes visible to that user, depending on the role they are assigned:",
            [
                "Owner / Editor: full visibility of the trip, including title, destinations, dates, all itinerary items (including costs and notes), and the username of every other member of the trip.",
                "Viewer: read-only visibility of the same information.",
            ],
            "When you accept an invitation to access another user's trip, your username (and, where applicable, your profile picture) becomes visible to the trip's owner and to the other members of that trip.",
            "We do not share any of your other profile information (email address, last name, \"About\" text, etc.) with other users as part of trip sharing.",
            "Owners may revoke a member's access to a trip at any time. After revocation, the former member will no longer be able to view or edit the trip.",
        ],
    },
    {
        heading: "5.3 Legal and Regulatory Disclosures",
        body: [
            "We may disclose your personal information if we are required or authorised to do so by law, including:",
            [
                "To comply with a court order, subpoena, or other legal process;",
                "To respond to a lawful request from a government or regulatory body;",
                "To protect our legal rights, property, or safety, or the rights, property, or safety of our users or others;",
                "To investigate, prevent, or take action against suspected fraud, security issues, or violations of our Terms of Use.",
            ],
        ],
    },
    {
        heading: "5.4 Business Transfers",
        body: [
            "If TravelBuddy is acquired by, merged with, or sold to another entity, or if we sell, transfer, or assign substantially all of our assets, your personal information may be transferred as part of that transaction. We will notify you of any such change of control and your rights at that time.",
        ],
    },
    {
        heading: "6. International Data Transfers",
        body: [
            "Your uploaded images (profile pictures and location images) are stored in Australia (AWS S3, Asia Pacific (Sydney) region, ap-southeast-2).",
            "Your account and trip data is stored on our database server hosted by Render, which operates infrastructure in the United States. By using the Service, you consent to this transfer. We take reasonable steps to ensure Render handles your information consistently with the Australian Privacy Principles.",
        ],
    },
    {
        heading: "7. Data Retention",
        body: [
            "We retain your personal information only for as long as is necessary to provide the Service to you and to meet our legal obligations.",
            [
                "Account information is retained for as long as your account is active.",
                "Trip data is retained for as long as you keep the trip in your account. When you delete a trip, the trip and all of its itinerary items are permanently deleted from our database.",
                "When you delete your account, your account and all associated personal information (profile, trips, itinerary items) are permanently deleted from our database. Deleted data cannot be recovered.",
                "Server logs and security records are retained for up to 6 months and then deleted or anonymised.",
                "Enquiry messages are retained for up to 5 years in order to respond to and follow up on your enquiry (and in case of any related legal dispute), and then deleted.",
            ],
        ],
    },
    {
        heading: "8. Your Privacy Rights",
        body: [
            "Under the Privacy Act 1988 (Cth) and the Australian Privacy Principles, you have the following rights in respect of your personal information.",
        ],
    },
    {
        heading: "8.1 Right to Access",
        body: [
            "You may request a copy of the personal information we hold about you. Most of this information is already directly accessible to you through your account (your profile in Settings, and your trips in My Trips).",
        ],
    },
    {
        heading: "8.2 Right to Correction",
        body: [
            "You may correct your profile information at any time through the Settings page. If you believe other personal information we hold about you is inaccurate, out of date, incomplete, irrelevant, or misleading, you may ask us to correct it by contacting us.",
        ],
    },
    {
        heading: "8.3 Right to Deletion",
        body: [
            "You may delete your account at any time through the Settings page (Account and Password > Delete account). On deletion, your account and all personal information associated with it are permanently removed from our database.",
        ],
    },
    {
        heading: "8.4 Right to Withdraw Consent",
        body: [
            "Where we rely on your consent to process your personal information, you may withdraw that consent at any time. Withdrawing consent does not affect the lawfulness of any processing carried out before you withdrew it.",
        ],
    },
    {
        heading: "8.5 Right to Lodge a Complaint",
        body: [
            "If you believe we have breached the Australian Privacy Principles or mishandled your personal information, you may complain to us in writing using the contact details in Section 12. We will acknowledge your complaint within a reasonable period and will aim to respond substantively within 30 days.",
            "If you are not satisfied with our response, you may complain to the Office of the Australian Information Commissioner (OAIC):",
            "Website: oaic.gov.au | Phone: 1300 363 992",
        ],
    },
    {
        heading: "9. How to Make a Request or Complaint",
        body: [
            "To make a privacy-related request (access, correction, deletion outside the in-app flow, or any other privacy concern), please contact us at info@thetravelbuddy.com.au with the subject line \"Privacy Request\".",
            "To help us respond, please include:",
            [
                "Your username and the email address linked to your account;",
                "A clear description of the request you are making;",
                "Any additional information that helps us identify the personal information involved.",
            ],
            "We may need to verify your identity before processing certain requests.",
            "We aim to respond to privacy requests within 30 days. There is no charge for making a privacy request, although we may charge a reasonable fee in limited circumstances (for example, if a request is manifestly unfounded or excessive — in which case we will tell you in advance).",
        ],
    },
    {
        heading: "10. Children's Privacy",
        body: [
            "The Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13.",
            "If you are a parent or guardian and you believe your child has provided personal information to us, please contact us using the details in Section 12 and we will take steps to delete that information.",
            "If you are between 13 and 18 years of age, you must use the Service with the consent of a parent or legal guardian.",
        ],
    },
    {
        heading: "11. Changes to This Privacy Policy",
        body: [
            "We may update this Privacy Policy from time to time to reflect changes to the Service, applicable law, or our information-handling practices.",
            "When we make material changes, we will:",
            [
                "Update the \"Last updated\" date at the top of this document;",
                "Where appropriate, notify you by email or via a prominent notice within the Service before the changes take effect.",
            ],
            "Your continued use of the Service after any changes become effective means you accept the updated Privacy Policy. If you do not agree to the changes, you should stop using the Service and may delete your account.",
        ],
    },
    {
        heading: "12. Contact Us",
        body: [
            "If you have any questions, requests, or concerns about this Privacy Policy or how we handle your personal information, please contact us:",
            "Email: info@thetravelbuddy.com.au",
            "Phone: +61 402 420 062",
            "Website: thetravelbuddy.com.au",
            "Privacy Officer: Ollie Le — info@thetravelbuddy.com.au",
        ],
    },
];
